import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ContentLoader, List } from 'shared/components';
import { displayValue, formatDate, hasPermission } from 'shared/helpers';
import { fetchAccountOrders } from '../../../actions';
import AddOrdersModal from './AddOrdersModal';
import RemoveOrdersModal from './RemoveOrdersModal';
import '../../styles.scss';

const PAGE_SIZE = 20;

const OrdersTab = ({ accountId }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'created_at' },
  ]);
  const [retryToken, setRetryToken] = useState(0);
  const [isAddOrdersModalOpen, setAddOrdersModalOpen] = useState(false);
  const [isRemoveOrdersModalOpen, setRemoveOrdersModalOpen] = useState(false);
  const canManageCustomerAccounts = hasPermission('manage_customer_accounts');

  const handleRowClick = (row) => {
    const orderId = get(row, 'id');
    if (orderId) {
      history.push(`/orders/${orderId}`);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    let isStale = false;
    setLoading(true);
    setError(false);
    fetchAccountOrders(accountId, {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      orderBy: currentSort,
    })
      .then((res) => {
        const payload = get(res, 'data');
        const results = Array.isArray(payload)
          ? payload
          : get(payload, 'results', []);
        const totalCount = Array.isArray(payload)
          ? results.length
          : get(payload, 'count', results.length);
        if (!isStale) {
          setOrders(results);
          setCount(totalCount);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!isStale) {
          setError(true);
          setOrders([]);
          setCount(0);
          setLoading(false);
        }
      });
  }, [accountId, currentSort, page, retryToken]);

  const handleRetry = () => setRetryToken((token) => token + 1);

  const handleRemoveOrdersSuccess = () => {
    handleRetry();
    setRemoveOrdersModalOpen(false);
  };

  const columns = [
    {
      Header: t('Created on'),
      accessor: 'created_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 180,
    },
    {
      Header: t('Order ID'),
      accessor: 'store_id',
      Cell: (cellData) => displayValue(cellData.value),
    },
    {
      Header: t('Campaign parameters'),
      id: 'campaign_parameters',
      accessor: (row) =>
        get(row, 'campaign_parameters') ??
        get(row, 'campaign_params') ??
        get(row, 'campaign') ??
        null,
      Cell: (cellData) => displayValue(cellData.value),
      maxWidth: 350,
    },
    {
      Header: t('Download ID'),
      id: 'download_id',
      accessor: (row) =>
        get(row, 'download_id') ??
        get(row, 'downloadId') ??
        get(row, 'download') ??
        null,
      Cell: (cellData) => displayValue(cellData.value),
      maxWidth: 300,
    },
  ];

  if (loading) {
    return <ContentLoader text={t('Loading orders')} />;
  }

  if (error) {
    return (
      <div className="CustomerAccount-error">
        <p>{t('Unable to load orders for this account')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div className="CustomerAccount-empty">
        <p>{t('No orders for this account')}</p>
        {canManageCustomerAccounts && (
          <Button
            theme="default"
            size="sm"
            onClick={() => setAddOrdersModalOpen(true)}
          >
            {t('Add orders')}
          </Button>
        )}
        {isAddOrdersModalOpen && (
          <AddOrdersModal
            accountId={accountId}
            closeCb={() => setAddOrdersModalOpen(false)}
            confirmCb={handleRetry}
          />
        )}
      </div>
    );
  }

  const pages = Math.ceil(count / PAGE_SIZE);

  return (
    <div className="CustomerAccount-list">
      <div className="button-group">
        {canManageCustomerAccounts && (
          <>
            <Button
              theme="default"
              size="sm"
              onClick={() => setAddOrdersModalOpen(true)}
            >
              {t('Add orders')}
            </Button>
            <Button
              theme="error"
              size="sm"
              onClick={() => setRemoveOrdersModalOpen(true)}
            >
              {t('Remove orders')}
            </Button>
          </>
        )}
      </div>
      <List
        data={orders}
        columns={columns}
        page={page}
        pages={pages}
        manual
        minRows={orders.length || 5}
        showPagination={pages > 1}
        loading={loading}
        onPageChange={(newPage) => setPage(newPage)}
        onSortedChange={(newSorted) => setCurrentSort(newSorted)}
        defaultSorted={currentSort}
        defaultPageSize={PAGE_SIZE}
        getTrProps={(state, rowInfo) => ({
          onClick: () => handleRowClick(rowInfo.original),
          style: { cursor: 'pointer' },
        })}
      />
      {isAddOrdersModalOpen && (
        <AddOrdersModal
          accountId={accountId}
          closeCb={() => setAddOrdersModalOpen(false)}
          confirmCb={handleRetry}
        />
      )}
      {isRemoveOrdersModalOpen && (
        <RemoveOrdersModal
          accountId={accountId}
          closeCb={() => setRemoveOrdersModalOpen(false)}
          confirmCb={handleRemoveOrdersSuccess}
        />
      )}
    </div>
  );
};

OrdersTab.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default OrdersTab;
